import { AlertTriangle, BookCopy } from "lucide-react";
import { BrandLoader } from "@/components/brand";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FC
} from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";

import {
  downloadPacketPdf,
  getPacketPdf,
  type PacketEditorSaveResult
} from "../../../api/packets-v2";
import { FilesystemAPI } from "../../caseFiles/api/filesystemApi";
import type { PacketManifest } from "../../caseFiles/types";
import { useCaseIde, useOnInvalidate } from "../context/CaseIdeContext";
import { useCaseData } from "../data/CaseDataContext";
import { saveBlob } from "../files/useZipDownload";
import type { EditTarget } from "../pdfEditor/editTarget";
import type { SnippetAction } from "../pdfSnippet/toolbar";

// PDFium is a large bundle; only opening a packet pays for it.
const PdfViewer = lazy(() =>
  import("../pdfViewer/PdfViewer").then((m) => ({ default: m.PdfViewer }))
);
const PdfEditor = lazy(() =>
  import("../pdfEditor/PdfEditor").then((m) => ({ default: m.PdfEditor }))
);

const PACKET_NAME = "Case packet";

/** What Build Packet sends: the same assembly Casey runs when asked. */
export const BUILD_PACKET_PROMPT = "Build the filing packet.";

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** "12 exhibits · 148 pages", from the packet's page map. */
export const packetSummary = (manifest: PacketManifest): string => {
  const exhibits = manifest.entries.filter((e) => e.kind === "file").length;
  return [
    exhibits ? plural(exhibits, "exhibit") : null,
    manifest.total_pages ? plural(manifest.total_pages, "page") : null
  ]
    .filter(Boolean)
    .join(" · ");
};

const ViewerLoading: FC<{ label?: string }> = ({
  label = "Loading the PDF viewer"
}) => (
  <div className="flex h-full items-center justify-center">
    <BrandLoader label={label} />
  </div>
);

type Props = {
  packetId: string;
  /** The packet's generation status from the workspace; null when unknown. */
  ready: boolean;
  page?: number | null;
};

/** What the editor was opened on; later reloads don't swap it underneath. */
type EditSession = { file: Blob; target: EditTarget; page: number | null };

/**
 * The assembled packet as one continuous PDF. Its exhibits are the PDF's own
 * bookmarks (written when the packet is built), so the viewer's Outline lists
 * them; the manifest feeds the stale warning and, for Edit, says which
 * exhibit or tab each page is so the editor's save lands on the case files.
 */
export const PacketPane: FC<Props> = ({ packetId, ready, page }) => {
  const { setSelectedText, invalidate, requestSend } = useCaseIde();
  const buildPacket = useCallback(
    () => requestSend(BUILD_PACKET_PROMPT),
    [requestSend]
  );
  const { fs } = useCaseData();

  const [pdf, setPdf] = useState<Blob | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [manifest, setManifest] = useState<PacketManifest | null>(null);
  const [isStale, setIsStale] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [viewerPage, setViewerPage] = useState<number | null>(null);
  const [session, setSession] = useState<EditSession | null>(null);
  const [dirty, setDirty] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  useOnInvalidate(
    ["packet", "fs"],
    useCallback(() => setReloadKey((k) => k + 1), [])
  );

  useEffect(() => {
    if (!ready) {
      setPdf(null);
      setManifest(null);
      setIsStale(false);
      return undefined;
    }
    let cancelled = false;
    setPdfError(null);
    getPacketPdf(packetId)
      .then((blob) => !cancelled && setPdf(blob))
      .catch(() => !cancelled && setPdfError("Couldn't load the packet PDF."));
    FilesystemAPI.getPacketManifest(packetId)
      .then((r) => {
        if (cancelled) return;
        setManifest(r.manifest);
        setIsStale(Boolean(r.is_stale));
      })
      .catch(() => {
        if (cancelled) return;
        setManifest(null);
        setIsStale(false);
      });
    return () => {
      cancelled = true;
    };
  }, [packetId, ready, reloadKey]);

  useEffect(() => () => setSelectedText(null), [setSelectedText]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const startEditing = useCallback(() => {
    if (!pdf || !manifest) return;
    const editedTabs = new Set(
      fs.nodes.filter((n) => n.divider_title_override).map((n) => n.id)
    );
    setSession({
      file: pdf,
      target: {
        kind: "packet",
        packetId,
        manifest,
        name: PACKET_NAME,
        editedTabs
      },
      page: viewerPage ?? page ?? null
    });
  }, [pdf, manifest, packetId, viewerPage, page, fs.nodes]);

  const exitEditor = useCallback(() => {
    setSession(null);
    setDirty(false);
    setConfirmDiscard(false);
  }, []);

  const requestCancel = useCallback(() => {
    if (dirty) setConfirmDiscard(true);
    else exitEditor();
  }, [dirty, exitEditor]);

  const handleSaved = useCallback(
    (result: PacketEditorSaveResult) => {
      exitEditor();
      void fs.refresh();
      invalidate(["packet", "fs"]);
      const files = result.changes.filter((c) => c.kind === "file").length;
      if (result.rebuild_error) {
        toast.warning(result.rebuild_error);
        return;
      }
      toast.success("Saved your packet edits. Rebuilding the packet…", {
        description: files
          ? `${files} exhibit file${
              files === 1 ? " has" : "s have"
            } a new version.`
          : undefined
      });
    },
    [exitEditor, fs, invalidate]
  );

  // The server's copy, not the viewer's: its divider titles are page text
  // Acrobat can edit, where the viewer's are the annotations Edit PDF retypes.
  const download = useCallback(() => {
    downloadPacketPdf(packetId)
      .then((blob) => saveBlob(blob, `${PACKET_NAME}.pdf`))
      .catch(() => toast.error("Couldn't download the packet."));
  }, [packetId]);

  // Edit needs the page map to say which exhibit each page is.
  const actions = useMemo<SnippetAction[]>(
    () => [
      {
        id: "download",
        label: "Download",
        icon: "download",
        onSelect: download
      },
      ...(manifest
        ? [
            {
              id: "edit",
              label: "Edit PDF",
              icon: "pencilMarker",
              onSelect: startEditing
            }
          ]
        : [])
    ],
    [download, manifest, startEditing]
  );

  if (!ready) {
    return (
      <EmptyState
        icon={BookCopy}
        className="h-full"
        title="No Packet Yet"
        description="Casey titles the exhibits, then assembles the packet in tree order."
        action={
          <Button
            variant="primary"
            size="sm"
            onClick={buildPacket}
            data-testid="packet-pane-build"
          >
            Build Packet
          </Button>
        }
      />
    );
  }

  if (session) {
    return (
      <div
        className="flex h-full min-h-0 flex-col"
        data-testid="packet-pane-editing"
      >
        <Suspense fallback={<ViewerLoading label="Loading the PDF editor" />}>
          <PdfEditor
            target={session.target}
            file={session.file}
            initialPage={session.page}
            onPacketSaved={handleSaved}
            onCancel={requestCancel}
            onDirtyChange={setDirty}
          />
        </Suspense>
        <AlertDialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Discard your changes?</AlertDialogTitle>
              <AlertDialogDescription>
                Nothing has been saved to the packet. Your edits will be lost.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep editing</AlertDialogCancel>
              <AlertDialogAction onClick={exitEditor}>
                Discard
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        className="flex h-8 shrink-0 items-center gap-2 border-b border-border px-3"
        data-testid="packet-pane-title"
      >
        <h2 className="truncate text-xs font-medium text-foreground">
          {PACKET_NAME}
        </h2>
        {manifest ? (
          <span className="truncate text-xs text-muted-foreground">
            {packetSummary(manifest)}
          </span>
        ) : null}
        <Button
          variant="ghost"
          size="xs"
          className="ml-auto"
          onClick={buildPacket}
          data-testid="packet-pane-build"
        >
          Build Packet
        </Button>
      </div>
      {isStale ? (
        <div className="flex h-7 shrink-0 items-center gap-1 border-b border-border px-3 text-xs text-warn">
          <AlertTriangle className="size-3 shrink-0" />
          <span className="truncate">
            Files changed since this packet was built; regenerate to refresh
            page numbers.
          </span>
        </div>
      ) : null}
      <div className="min-h-0 flex-1">
        {pdfError ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            {pdfError}
          </div>
        ) : (
          <Suspense fallback={<ViewerLoading />}>
            <PdfViewer
              file={pdf}
              ariaLabel="Case packet"
              emptyText="Loading packet…"
              downloadName={`${PACKET_NAME}.pdf`}
              scrollToPageIndex={page ? page - 1 : null}
              onVisiblePageChange={setViewerPage}
              onTextSelectionChange={setSelectedText}
              actions={actions}
              hideDownload
            />
          </Suspense>
        )}
      </div>
    </div>
  );
};
